import { Card } from "@/components/ui/card";
import { Check } from "lucide-react";
import type { Block } from "@/types/proposals";

interface GoodBetterBestBlockProps {
  block: Block;
  onUpdate: (updates: Partial<Block>) => void;
  onSelect: () => void;
  smartFieldData?: Record<string, any>;
}

export function GoodBetterBestBlock({ block, onSelect, smartFieldData }: GoodBetterBestBlockProps) {
  const { options = [], useDynamicData, goodMultiplier = 1.0, betterMultiplier = 1.2, bestMultiplier = 1.4 } = block.content;
  
  // Calculate dynamic pricing if enabled
  let displayOptions = options;
  if (useDynamicData && smartFieldData?.estimateTotal) {
    const basePrice = smartFieldData.estimateTotal;
    displayOptions = [
      { tier: "Good", price: `$${(basePrice * goodMultiplier).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, features: options[0]?.features || ["Standard materials", "Basic warranty", "Standard installation"] },
      { tier: "Better", price: `$${(basePrice * betterMultiplier).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, features: options[1]?.features || ["Premium materials", "Enhanced warranty", "Premium installation", "Extra protection"] },
      { tier: "Best", price: `$${(basePrice * bestMultiplier).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, features: options[2]?.features || ["Top-tier materials", "Lifetime warranty", "Premium installation", "Maximum protection", "Extended coverage"] },
    ];
  } else if (!useDynamicData && options.length === 0) {
    // Fallback static options
    displayOptions = [
      { tier: "Good", price: "$8,500", features: ["25-year shingles", "Basic warranty", "Standard installation"] },
      { tier: "Better", price: "$11,500", features: ["30-year shingles", "Enhanced warranty", "Premium installation", "Ventilation upgrade"] },
      { tier: "Best", price: "$15,500", features: ["Lifetime shingles", "Lifetime warranty", "Premium installation", "Full ventilation system", "Extended coverage"] },
    ];
  }

  return (
    <div className="cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all rounded p-6" onClick={onSelect}>
      {useDynamicData && !smartFieldData?.estimateTotal ? (
        <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded bg-blue-50">
          <p className="font-semibold">💎 Dynamic Pricing Tiers</p>
          <p className="text-sm mt-1">Will calculate Good/Better/Best options from estimate when linked to a job</p>
        </div>
      ) : (
        <>
          <h3 className="text-2xl font-bold text-center mb-6">Choose Your Package</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayOptions.map((option: any, index: number) => (
              <Card key={index} className={`p-6 ${index === 1 ? 'border-primary border-2' : ''}`}>
                <div className="text-center mb-4">
                  <h4 className="text-xl font-bold mb-2">{option.tier}</h4>
                  <p className="text-3xl font-bold text-primary">{option.price}</p>
                </div>
                <ul className="space-y-2">
                  {option.features.map((feature: string, i: number) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
